/**
 * Video play/pause toggle — self-hosted looping video (E20-1067)
 *
 * A looping video plays muted with no native controls. This custom element wires a
 * pause/play button so users can stop the motion (WCAG 2.2.2), and starts the video
 * paused when the user prefers reduced motion — an enhancement, not a conformance
 * requirement (SC 2.3.3 covers interaction-triggered animation, not autoplaying video).
 *
 * Shared by Media Spotlight (media area) and Media Header (background video); the
 * button markup comes from the bfx-media-playtoggle snippet.
 */
class BfxVideoToggle extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.toggle = this.querySelector('[data-media-toggle]');
    if (!this.video || !this.toggle) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    // Playback is JS-driven (no autoplay attribute) so reduced-motion users never
    // see initial motion. Start playing only when motion is allowed; otherwise the
    // video stays paused and the user can start it with the button.
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.video.play()?.catch(() => {});
    }

    this.toggle.addEventListener('click', () => this.togglePlayback(), { signal });
    this.video.addEventListener('play', () => this.sync(), { signal });
    this.video.addEventListener('pause', () => this.sync(), { signal });

    this.sync();
  }

  togglePlayback() {
    if (this.video.paused) {
      // play() returns a promise that can reject (e.g. autoplay blocked); swallow it.
      this.video.play()?.catch(() => {});
    } else {
      this.video.pause();
    }
  }

  sync() {
    const paused = this.video.paused;
    this.classList.toggle('is-paused', paused);
    // The label describes the action (Play when paused, Pause when playing) — the
    // standard play/pause pattern, so no aria-pressed (which would double-encode state).
    const label = paused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause;
    if (label) this.toggle.setAttribute('aria-label', label);
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }
}

if (!customElements.get('bfx-video-toggle')) {
  customElements.define('bfx-video-toggle', BfxVideoToggle);
}
